import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ReactFlow,
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  Panel,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import LZString from 'lz-string';
import { SKILLS_DEFAULTS } from './data/skills-defaults';

const V_GAP = 170;
const H_GAP = 250;
const CANVAS_CENTER_X = 400;
const CANVAS_TOP_Y = 60;

/**
 * Sugiyama-style layered layout with barycenter crossing minimisation.
 * items: Array<{ id: string, parentIds: string[] }>
 * Returns: { [id]: { x: number, y: number } }
 */
function computeLayeredLayout(items) {
  // 1. Longest-path layer assignment
  const depthMap = {};
  const computeDepth = (id, visited = new Set()) => {
    if (depthMap[id] !== undefined) return depthMap[id];
    if (visited.has(id)) return 0;
    visited.add(id);
    const node = items.find((w) => w.id === id);
    if (!node || !node.parentIds?.length) return (depthMap[id] = 0);
    const maxParent = Math.max(...node.parentIds.map((pid) => computeDepth(pid, new Set(visited))));
    return (depthMap[id] = maxParent + 1);
  };
  items.forEach((w) => computeDepth(w.id));

  const maxLayer = items.reduce((m, w) => Math.max(m, depthMap[w.id] ?? 0), 0);

  // 2. Build mutable layer arrays
  const layerArrays = Array.from({ length: maxLayer + 1 }, () => []);
  items.forEach((w) => layerArrays[depthMap[w.id] ?? 0].push(w.id));

  // 3. Initial x-rank: index within layer
  const xRank = {};
  layerArrays.forEach((layer) => layer.forEach((id, i) => { xRank[id] = i; }));

  // 4. Three down+up barycenter passes
  for (let pass = 0; pass < 3; pass++) {
    // Down sweep: sort by mean x-rank of parents
    for (let li = 1; li <= maxLayer; li++) {
      const layer = layerArrays[li];
      const bary = {};
      layer.forEach((id) => {
        const node = items.find((n) => n.id === id);
        const pIds = (node?.parentIds || []).filter((pid) => (depthMap[pid] ?? 0) < li);
        bary[id] = pIds.length === 0
          ? (xRank[id] ?? 0)
          : pIds.reduce((s, pid) => s + (xRank[pid] ?? 0), 0) / pIds.length;
      });
      layer.sort((a, b) => bary[a] - bary[b]);
      layer.forEach((id, i) => { xRank[id] = i; });
    }
    // Up sweep: sort by mean x-rank of children
    for (let li = maxLayer - 1; li >= 0; li--) {
      const layer = layerArrays[li];
      const childrenOf = {};
      layer.forEach((id) => { childrenOf[id] = []; });
      (layerArrays[li + 1] || []).forEach((childId) => {
        const node = items.find((n) => n.id === childId);
        (node?.parentIds || []).forEach((pid) => {
          if (childrenOf[pid] !== undefined) childrenOf[pid].push(childId);
        });
      });
      const bary = {};
      layer.forEach((id) => {
        const cIds = childrenOf[id];
        bary[id] = cIds.length === 0
          ? (xRank[id] ?? 0)
          : cIds.reduce((s, cid) => s + (xRank[cid] ?? 0), 0) / cIds.length;
      });
      layer.sort((a, b) => bary[a] - bary[b]);
      layer.forEach((id, i) => { xRank[id] = i; });
    }
  }

  // 5. Convert rank → pixel coordinates, centering each layer
  const positions = {};
  layerArrays.forEach((layer, li) => {
    const total = layer.length;
    layer.forEach((id, i) => {
      positions[id] = {
        x: (i - (total - 1) / 2) * H_GAP + CANVAS_CENTER_X,
        y: li * V_GAP + CANVAS_TOP_Y,
      };
    });
  });
  return positions;
}

function computeAutoLayout(nodes, edges) {
  const items = nodes.map((n) => ({
    id: n.id,
    parentIds: edges.filter((e) => e.target === n.id).map((e) => e.source),
  }));
  const positions = computeLayeredLayout(items);
  return nodes.map((node) => ({
    ...node,
    position: positions[node.id] || node.position,
  }));
}

/**
 * Builds the canonical export object from the current canvas state.
 * Includes node positions so the layout can be fully restored on import.
 */
function serializeCanvas(nodes, edges, originalWorkflow) {
  const workflow = flowToWorkflow(nodes, edges, originalWorkflow);
  const positionMap = Object.fromEntries(nodes.map((n) => [n.id, n.position]));
  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    nodes: workflow.map((w) => ({
      id: w.id,
      name: w.name || w.id,
      model: w.model || 'gpt-4o',
      provider: w.provider || 'openai',
      systemPrompt: w.systemPrompt || '',
      tools: Array.isArray(w.tools) ? w.tools : [],
      parentIds: w.parentIds || [],
      passFullHistoryParents: w.passFullHistoryParents || [],
      position: positionMap[w.id] || { x: 0, y: 0 },
    })),
  };
}

function workflowToFlow(workflow, pipelineStatus) {
  const items = workflow.map((w) => ({ id: w.id, parentIds: w.parentIds || [] }));
  const positions = computeLayeredLayout(items);

  const nodes = workflow.map((w) => {
    return {
      id: w.id,
      type: 'agentNode',
      position: positions[w.id] || { x: 0, y: 0 },
      data: {
        label: w.name,
        model: w.model || 'gpt-4o',
        provider: w.provider || 'openai',
        workflow: w,
        status: pipelineStatus?.[w.id]?.status || 'idle',
      },
      draggable: true,
    };
  });

  const edges = [];
  workflow.forEach((w) => {
    (w.parentIds || []).forEach((pid) => {
      const passFullHistory = (w.passFullHistoryParents || []).includes(pid);
      edges.push({
        id: `${pid}->${w.id}`,
        source: pid,
        target: w.id,
        type: 'context',
        animated: true,
        data: { passFullHistory },
        style: { stroke: '#00f2ff', strokeWidth: 2 },
        markerEnd: { type: 'arrowclosed', color: '#00f2ff' },
      });
    });
  });

  return { nodes, edges };
}

function flowToWorkflow(nodes, edges, originalWorkflow) {
  const workflow = nodes.map((node) => {
    const original = originalWorkflow.find((w) => w.id === node.id);
    const incomingEdges = edges.filter((e) => e.target === node.id);
    const parentIds = incomingEdges.map((e) => e.source);
    const passFullHistoryParents = incomingEdges
      .filter((e) => e.data?.passFullHistory === true)
      .map((e) => e.source);
    return {
      ...(original || node.data.workflow || {}),
      id: node.id,
      name: node.data.label,
      model: node.data.model,
      provider: node.data.provider || 'openai',
      parentIds,
      passFullHistoryParents,
    };
  });

  // Topological sort — roots first, then by dependency order
  const sorted = [];
  const visited = new Set();

  const visit = (id) => {
    if (visited.has(id)) return;
    visited.add(id);
    const node = workflow.find((w) => w.id === id);
    if (!node) return;
    // Visit all parents first
    (node.parentIds || []).forEach((pid) => visit(pid));
    sorted.push(node);
  };

  workflow.forEach((w) => visit(w.id));
  return sorted;
}

function getShortModel(model) {
  if (!model) return '';
  if (model.includes('gpt-4o-mini')) return 'GPT-4o Mini';
  if (model.includes('gpt-4o')) return 'GPT-4o';
  if (model.includes('claude-haiku')) return 'Claude Haiku';
  if (model.includes('claude-sonnet')) return 'Claude Sonnet';
  if (model.includes('claude-opus')) return 'Claude Opus';
  if (model.includes('gemini-2.5-pro')) return 'Gemini 2.5 Pro';
  if (model.includes('gemini-2.5-flash')) return 'Gemini 2.5 Flash';
  if (model.includes('gemini')) return 'Gemini';
  return model.slice(0, 16);
}

function AgentNode({ data, selected }) {
  const providerColor = {
    openai: '#10a37f',
    anthropic: '#d4a574',
    gemini: '#4285f4',
  }[data.provider] || '#666';

  const statusColor = {
    active: '#00f2ff',
    completed: '#3fb950',
    error: '#f85149',
    retrying: '#d29922',
    idle: null,
  }[data.status];

  const isActive = data.status === 'active';
  const isCompleted = data.status === 'completed';
  const isError = data.status === 'error';

  return (
    <div
      className={`wf-node ${selected ? 'wf-node--selected' : ''} ${isActive ? 'wf-node--active' : ''} ${isCompleted ? 'wf-node--completed' : ''} ${isError ? 'wf-node--error' : ''}`}
      style={statusColor ? { borderColor: statusColor, boxShadow: `0 0 8px ${statusColor}44` } : {}}
    >
      <Handle type="target" position={Position.Top} className="wf-handle wf-handle--target" />
      <div className="wf-node-header" style={{ borderTopColor: statusColor || providerColor }}>
        <div className="wf-node-title-row">
          <span className="wf-node-name">{data.label}</span>
          {isActive && <span className="wf-node-status-pulse" />}
          {isCompleted && <span className="wf-node-status-check">✓</span>}
          {isError && <span className="wf-node-status-error">✕</span>}
        </div>
      </div>
      <div className="wf-node-model">{getShortModel(data.workflow?.model || data.model || 'gpt-4o')}</div>
      <Handle type="source" position={Position.Bottom} className="wf-handle wf-handle--source" />
      <div className="wf-node-hint">Click to edit prompt &amp; model</div>
    </div>
  );
}

const nodeTypes = { agentNode: AgentNode };

function ContextEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, style, markerEnd }) {
  const { setEdges } = useReactFlow();
  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const isFullHistory = data?.passFullHistory === true;

  const toggle = (evt) => {
    evt.stopPropagation();
    setEdges((eds) =>
      eds.map((e) =>
        e.id === id ? { ...e, data: { ...e.data, passFullHistory: !isFullHistory } } : e
      )
    );
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <button
          type="button"
          className={`wf-edge-toggle nodrag nopan${isFullHistory ? ' wf-edge-toggle--full' : ''}`}
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          onClick={toggle}
          title={isFullHistory ? 'Full history — click to switch to selective routing' : 'Selective routing — click to pass full history'}
        >
          {isFullHistory ? 'full ctx' : 'selective'}
        </button>
      </EdgeLabelRenderer>
    </>
  );
}

const edgeTypes = { context: ContextEdge };

export default function WorkflowEditor({ workflow, pipeline, onSave, onClose, pipelineStatus, loading }) {
  const { nodes: initNodes, edges: initEdges } = workflowToFlow(workflow, pipelineStatus);
  const [nodes, setNodes, onNodesChange] = useNodesState(initNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initEdges);
  const [panelOpen, setPanelOpen] = useState(true);
  const [shareCopied, setShareCopied] = useState(false);
  const shareCopiedTimer = useRef(null);

  useEffect(() => {
    if (!pipelineStatus) return;
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          status: pipelineStatus[node.id]?.status || 'idle',
        },
      }))
    );
  }, [pipelineStatus, setNodes]);

  useEffect(() => {
    if (!pipelineStatus) return;
    setEdges((eds) =>
      eds.map((edge) => {
        const targetStatus = pipelineStatus[edge.target]?.status;
        const sourceStatus = pipelineStatus[edge.source]?.status;
        const isLive = targetStatus === 'active' || sourceStatus === 'completed';
        return {
          ...edge,
          type: 'context',
          animated: isLive,
          style: {
            ...edge.style,
            stroke: isLive ? '#00f2ff' : '#2a2c38',
            strokeWidth: isLive ? 2.5 : 1.5,
          },
          markerEnd: { type: 'arrowclosed', color: isLive ? '#00f2ff' : '#2a2c38' },
        };
      })
    );
  }, [pipelineStatus, setEdges]);

  const addSkillNode = useCallback(
    (skill) => {
      const newId = `${skill.id}-${Date.now()}`;
      const newNode = {
        id: newId,
        type: 'agentNode',
        position: {
          x: Math.random() * 300 + 200,
          y: Math.random() * 200 + 100,
        },
        data: {
          label: skill.name,
          model: skill.recommendedModel || 'gpt-4o',
          provider: 'openai',
          workflow: {
            id: newId,
            name: skill.name,
            systemPrompt: skill.systemPrompt,
            model: skill.recommendedModel || 'gpt-4o',
            provider: 'openai',
            parentIds: [],
          },
        },
        draggable: true,
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [setNodes]
  );

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'context',
            animated: true,
            data: { passFullHistory: false },
            style: { stroke: '#00f2ff', strokeWidth: 2 },
            markerEnd: { type: 'arrowclosed', color: '#00f2ff' },
          },
          eds
        )
      ),
    [setEdges]
  );

  const handleAutoLayout = useCallback(() => {
    setNodes((nds) => computeAutoLayout(nds, edges));
  }, [edges, setNodes]);

  const handleExportJSON = useCallback(() => {
    const state = serializeCanvas(nodes, edges, workflow);
    const json = JSON.stringify(state, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [nodes, edges, workflow]);

  const handleShareLink = useCallback(() => {
    const state = serializeCanvas(nodes, edges, workflow);
    const compressed = LZString.compressToEncodedURIComponent(JSON.stringify(state));
    const base = window.location.origin + window.location.pathname + (window.location.search || '');
    const shareUrl = `${base}#w=${compressed}`;
    window.history.replaceState(null, '', `#w=${compressed}`);
    const copyText = () => {
      setShareCopied(true);
      clearTimeout(shareCopiedTimer.current);
      shareCopiedTimer.current = setTimeout(() => setShareCopied(false), 2200);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl).then(copyText).catch(() => {
        window.prompt('Copy this link to share your workflow:', shareUrl);
      });
    } else {
      window.prompt('Copy this link to share your workflow:', shareUrl);
      copyText();
    }
  }, [nodes, edges, workflow]);

  const handleSave = () => {
    const newWorkflow = flowToWorkflow(nodes, edges, workflow);
    onSave(newWorkflow);
  };

  return (
    <div className="wf-editor-overlay">
      <div className="wf-editor-container">
        <div className="wf-editor-header">
          <div className="wf-editor-title">
            <span>⚙️ Workflow Editor</span>
            <span className="wf-editor-subtitle">Drag to reposition · Connect nodes by dragging from ● to ● · Click an edge to delete it</span>
          </div>
          <div className="wf-editor-actions">
            <div className="wf-action-group">
              <button
                type="button"
                className="wf-btn wf-btn--secondary"
                onClick={handleExportJSON}
                title="Download workflow as a JSON file"
              >
                ↓ Export JSON
              </button>
              <button
                type="button"
                className={`wf-btn wf-btn--secondary wf-btn--share${shareCopied ? ' wf-btn--copied' : ''}`}
                onClick={handleShareLink}
                title="Compress workflow to a shareable URL and copy it to clipboard"
              >
                {shareCopied ? '✓ Copied!' : '⎗ Share link'}
              </button>
            </div>
            <div className="wf-action-divider" />
            <button type="button" className="wf-btn wf-btn--secondary" onClick={handleAutoLayout} title="Snap nodes into a clean top-down hierarchy">
              ⬡ Auto Layout
            </button>
            <button type="button" className="wf-btn wf-btn--secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="wf-btn wf-btn--primary" onClick={handleSave}>
              Save workflow
            </button>
          </div>
        </div>
        <div className="wf-editor-body">
          <div className="wf-editor-canvas">
            <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.4, minZoom: 0.5, maxZoom: 1.5 }}
            deleteKeyCode="Backspace"
            style={{ background: '#0d0e10' }}
            connectionLineStyle={{ stroke: '#00f2ff', strokeWidth: 2 }}
            defaultEdgeOptions={{
              type: 'context',
              animated: true,
              data: { passFullHistory: false },
              style: { stroke: '#00f2ff', strokeWidth: 2 },
            }}
          >
            <Background color="#1a1b1e" gap={24} size={1} variant="dots" />
            <Controls className="wf-controls" position="top-left" />
            <MiniMap className="wf-minimap" position="bottom-left" nodeColor={() => '#00f2ff22'} maskColor="rgba(0,0,0,0.6)" />
            <Panel position="bottom-center" className="wf-legend">
              <span>Drag from bottom ● of a node to top ● of another to connect · Backspace to delete selected edge</span>
            </Panel>
          </ReactFlow>
          </div>
          {panelOpen && (
            <div className="wf-agent-panel">
              <div className="wf-panel-header">
                <span>Add agents</span>
                <button className="wf-panel-close" onClick={() => setPanelOpen(false)} type="button">
                  ✕
                </button>
              </div>
              <div className="wf-panel-list">
                {SKILLS_DEFAULTS.map((skill) => (
                  <button
                    key={skill.id}
                    type="button"
                    className="wf-panel-skill"
                    onClick={() => addSkillNode(skill)}
                  >
                    <span className="wf-panel-skill-name">{skill.name}</span>
                    <span className="wf-panel-skill-tags">
                      {(skill.tags || []).map((t) => (
                        <span key={t} className="wf-panel-tag">
                          {t}
                        </span>
                      ))}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!panelOpen && (
            <button type="button" className="wf-panel-reopen" onClick={() => setPanelOpen(true)}>
              + Agents
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
